import { useState, useEffect } from "react";
import {
    LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend, BarChart, Bar, ResponsiveContainer,
} from "recharts";
import useApi from "../customHooks/useAPI";

const HealthGraphs = ({ patientID, defaultValueProp }) => {
    const [type, setType] = useState(defaultValueProp);
    const [data, setData] = useState([]);
    const {callApi, loading} = useApi();

    const CustomTooltip = ({ active, payload }) => {
        if (!active || !payload || payload.length === 0) return null;

        const item = payload[0].payload; // common data object

        return (
            <div className="bg-white shadow rounded p-2 border">

                {/* FULL DATE/TIME */}
                <p className="font-semibold">{item.fullDate}</p>

                {/* LOOP OVER ALL LINES (systolic, diastolic, heartbeat...) */}
                {payload.map((entry, index) => (
                    <p key={index} style={{ color: entry.color }}>
                        {entry.name}: {entry.value}
                    </p>
                ))}

                {/* Optional extra fields */}
                {item.insulinName && <p>Insulin Name: {item.insulinName}</p>}
                {item.patientState && <p>State: {item.patientState}</p>}
            </div>
        );
    };



    useEffect(() => {
        const fetchData = async () => {
            try {
                const json = await callApi({
                    url: `${process.env.REACT_APP_BACKEND_ENDPOINT}/patient/getHealthData?type=${type}`,
                    method: "GET",
                    headers: { "Patient-Id": patientID }
                });

                if (json?.data) {
                    // format date for X axis
                    const formatted = json.data.map((item) => {
                        const d = new Date(item.stampingTime);

                        return {
                            ...item,
                            date: d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" }),
                            fullDate: d.toLocaleString("en-GB", {
                                day: "2-digit",
                                month: "long",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                            })
                        };
                    });
                    setData(formatted.reverse());
                }
            } catch (err) {
                console.error("Error fetching data:", err);
            }
        };

        fetchData();
    }, [type]);

    const renderChart = () => {
        if (data.length === 0) return <p className="text-gray-500">No data available</p>;

        switch (type) {
            case "GLUCOSE":
                return (
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={data}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" />
                            <YAxis />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend />
                            <Line type="monotone" dataKey="glucose" stroke="#2563eb" name="Glucose (mg/dL)" />
                        </LineChart>
                    </ResponsiveContainer>
                );

            case "INSULIN":
                return (
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={data}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" />
                            <YAxis />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend />
                            <Bar dataKey="insulinUnits" fill="#10b981" name="Insulin Units" />
                        </BarChart>
                    </ResponsiveContainer>
                );

            case "WEIGHT":
                return (
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={data}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" />
                            <YAxis />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend />
                            <Line type="monotone" dataKey="weightInKgs" stroke="#f59e0b" name="Weight (kg)" />
                        </LineChart>
                    </ResponsiveContainer>
                );

            case "BLOOD_PRESSURE":
                return (
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={data}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" />
                            <YAxis />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend />
                            <Line type="monotone" dataKey="systolic" stroke="#ef4444" name="Systolic" />
                            <Line type="monotone" dataKey="diastolic" stroke="#3b82f6" name="Diastolic" />
                            <Line type="monotone" dataKey="heartBeatPerMin" stroke="#22c55e" name="Heartbeat" />
                        </LineChart>
                    </ResponsiveContainer>
                );

            default:
                return null;
        }
    };

    return (
        <div>
            <div className="flex justify-between items-center mb-4">
                <select
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                    className="border rounded-md p-2 text-sm"
                >
                    <option value="GLUCOSE">Glucose</option>
                    <option value="INSULIN">Insulin</option>
                    <option value="WEIGHT">Weight</option>
                    <option value="BLOOD_PRESSURE">Blood Pressure</option>
                </select>
            </div>

            {loading ? (
                <p className="text-gray-500">Loading...</p>
            ) : (
                renderChart()
            )}
        </div>
    );
};

export default HealthGraphs;
